import { SearchTerm } from '../models/search/SearchTerm';
import { MusteringReportTotals } from '../models/MusteringReportTotals';
import { Translator } from '../contexts/translator';
import { TranslationKeys } from '../contexts/TranslationKeys';
import { toast } from 'react-toastify';
import basePdfStyles from './pdf-styles/base.css?raw';
import defaultPdfBrandStyles from './pdf-styles/default.css?raw';
import improPdfBrandStyles from './pdf-styles/impro.css?raw';
import pmtPdfBrandStyles from './pdf-styles/pmt.css?raw';

const PDF_BRAND_STYLES: Record<string, string> = {
    default: defaultPdfBrandStyles,
    impro: improPdfBrandStyles,
    pmt: pmtPdfBrandStyles,
};

// Function to detect RTL (Right-to-Left) languages
const isRTLText = (text: string): boolean => {
    // Hebrew: \u0590-\u05FF
    // Arabic: \u0600-\u06FF, \u0750-\u077F, \u08A0-\u08FF
    const rtlRegex = /[\u0590-\u05FF\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF]/;
    return rtlRegex.test(text);
};

const getPdfBrandStyles = (): string => {
    const brand = import.meta.env.VITE_BRAND_PATH || 'default';
    return PDF_BRAND_STYLES[brand] || PDF_BRAND_STYLES.default;
};

const getPdfDocumentLanguage = (): string => {
    return window.sessionStorage.getItem('selectedLanguage') || 'en';
};

// Function to generate HTML for the report
const generateReportHTML = (
    title: string,
    description: string,
    user: string,
    columns: any[],
    data: any[],
    date: string,
    translate: Translator,
    appliedFilters?: any[],
    searchTerm?: SearchTerm,
    musteringTotals?: MusteringReportTotals | undefined,
): string => {
    const delimiter = " : ";
    const recordCount = data.length;
    const pdfStyles = `${basePdfStyles}\n${getPdfBrandStyles()}`;
    const documentLanguage = getPdfDocumentLanguage();
    
    // Check if content contains RTL text
    const allText = [title, description, user, date, ...(appliedFilters || []), 
                     searchTerm?.value || '', searchTerm?.heading || ''].join(' ');
    const hasRTL = isRTLText(allText);
    
    // Generate table headers
    const tableHeaders = columns.map(col => `<th>${col || ''}</th>`).join('');

    const tableRows = data.map((row, index) =>
        `<tr data-row="${index + 1}">${row.map((cell: any) => {
            const cellValue = (cell === null || cell === undefined || cell === 'null') ? '' : String(cell).trim();
            return `<td${isRTLText(cellValue) ? ' class="rtl-text"' : ''}>${cellValue}</td>`;
        }).join('')}</tr>`
    ).join('');

    console.log(`Generating PDF with ${data.length} data rows and ${columns.length} columns`);

    return `
<!DOCTYPE html>
<html lang="${documentLanguage}" dir="${hasRTL ? 'rtl' : 'ltr'}">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>${title}</title>
    <style>
        ${pdfStyles}
    </style>
</head>
<body>
    <div class="report-container">
        <div class="report-header">
            <span class="report-title">${title}</span>
            <span class="report-info">${description}</span>
            <span class="report-info">${translate.byKeyFormatted(TranslationKeys.userWithUser, { delimiter, user })}</span>
            <span class="report-info">${translate.byKeyFormatted(TranslationKeys.dateWithDate, { delimiter, date })}</span>
            <span class="report-info">${translate.byKeyFormatted(TranslationKeys.rowCountWithRowCount, { delimiter, recordCount })}</span>
        </div>
        
        ${appliedFilters?.length ? `
        <div class="filters-section">
            <div class="filter-title">${translate.byKey(TranslationKeys.appliedFilters)}</div>
            ${appliedFilters.map(filter => {
                const filterKey = filter.substring(0, filter.indexOf(":")).trim();
                const filterValues = filter.substring(filter.indexOf(":") + 1).trim();
                return `
                    <div class="filter-item">
                        <span class="filter-key">${filterKey}</span>${delimiter}${filterValues}
                    </div>
                `;
            }).join('')}
        </div>
        ` : ''}

        ${searchTerm?.value && searchTerm?.heading && searchTerm?.value !== "" && searchTerm?.heading !== "" ? `
        <div class="search-term">
            ${translate.byKeyFormatted(TranslationKeys.appliedSearchTermAndHeadingWithAppliedSearchTermAndHeading, {
                delimiter,
                searchTermValue: searchTerm.value,
                searchTermHeading: searchTerm.heading
            })}
        </div>
        ` : ''}

        ${musteringTotals ? `
        <div class="totals-section">
            <div class="report-info">${translate.byKeyFormatted(TranslationKeys.totalPersonnelOnSiteWithValue, { delimiter, totalOnSite: musteringTotals.totalOnSite })}</div>
            <div class="report-info">${translate.byKeyFormatted(TranslationKeys.totalPersonnelMusteredWithValue, { delimiter, totalMustered: musteringTotals.totalMustered })}</div>
            <div class="report-info">${translate.byKeyFormatted(TranslationKeys.totalPersonnelUnaccountedForWithValue, { delimiter, totalUnAccountedWithExited: musteringTotals.unaccountedWithExited })}</div>
        </div>
        ` : ''}

        <table class="data-table">
            <thead>
                <tr>${tableHeaders}</tr>
            </thead>
            <tbody>
                ${tableRows}
            </tbody>
        </table>
    </div>
</body>
</html>`;
};

const printHtmlDocument = async (htmlContent: string, filename: string, translate: Translator): Promise<void> => {
    await new Promise<void>((resolve, reject) => {
        const iframe = document.createElement('iframe');
        iframe.style.position = 'fixed';
        iframe.style.width = '0';
        iframe.style.height = '0';
        iframe.style.border = '0';
        iframe.style.right = '0';
        iframe.style.bottom = '0';

        const cleanup = () => {
            iframe.remove();
        };

        iframe.onload = async () => {
            const printWindow = iframe.contentWindow;
            const printDocument = iframe.contentDocument;

            if (!printWindow || !printDocument) {
                cleanup();
                reject(new Error(translate.byKey(TranslationKeys.unableToOpenPrintPreview)));
                return;
            }

            const handleAfterPrint = () => {
                printWindow.removeEventListener('afterprint', handleAfterPrint);
                cleanup();
                resolve();
            };

            printWindow.addEventListener('afterprint', handleAfterPrint);

            try {
                printDocument.title = filename;
                if (printDocument.fonts?.ready) {
                    await printDocument.fonts.ready;
                }
                printWindow.focus();
                printWindow.print();
            } catch (error) {
                printWindow.removeEventListener('afterprint', handleAfterPrint);
                cleanup();
                reject(error instanceof Error ? error : new Error(translate.byKey(TranslationKeys.pdfPrintFailed)));
            }
        };

        document.body.appendChild(iframe);

        const iframeDocument = iframe.contentDocument;
        if (!iframeDocument) {
            cleanup();
            reject(new Error(translate.byKey(TranslationKeys.unableToCreatePrintDocument)));
            return;
        }

        iframeDocument.open();
        iframeDocument.write(htmlContent);
        iframeDocument.close();
    });
};

export const exportToPDF = async (
    filename: string,
    title: string,
    description: string,
    user: string,
    columns: any[],
    data: any[],
    date: string,
    translate: Translator,
    appliedFilters?: any[],
    searchTerm?: SearchTerm,
    musteringTotals?: Promise<MusteringReportTotals | undefined>,
) => {
    try {
        
        // For very large datasets, warn about potential issues
        if (data.length > 2000) {
            toast.warn(translate.byKeyFormatted(TranslationKeys.largeDatasetWarning, { length: data.length }));
        }
        
        // Enforce hard limit of 10,000 rows
        const exportData = data.length > 10000 ? data.slice(0, 10000) : data;
        if (data.length > 10000) {
            toast.warn(translate.byKeyFormatted(TranslationKeys.dataTruncatedReduceRowsToExport, { length: data.length }));
        }
        
        // Wait for musteringTotals to resolve
        const totals = await musteringTotals;
        
        // Generate HTML content for ALL data in single PDF
        const htmlContent = generateReportHTML(
            title,
            description,
            user,
            columns,
            exportData,
            date,
            translate,
            appliedFilters,
            searchTerm,
            totals
        );

        console.log(`Opening browser print dialog for ${exportData.length} rows...`);
        await printHtmlDocument(htmlContent, filename, translate);
        
    } catch (error) {
        throw error;
    }
};
